import {
  eachDayOfInterval,
  format,
  getWeek,
  getYear,
  isBefore,
  isSameDay,
  parseISO,
} from 'date-fns'

import { TRoomContractSchedule } from '@/entities/room'

type Range = Omit<TRoomContractSchedule, 'checkoutDate'>

export interface ProcessedRange extends Range {
  laneIndex: number // 세로 순서 (0부터 시작)
  colorIndex: number // 색상 인덱스 (0 or 1)
}

// 특정 날짜에 그려야 할 범위 정보
export interface ContractBarInfo {
  rangeId: number
  laneIndex: number // 전역 레인 인덱스 (원래 순서)
  weeklyLaneIndex: number // 주별 레인 인덱스 (재배치된 순서)
  colorIndex: number
  isStart: boolean // 시작일 여부
  isEnd: boolean // 종료일 여부
}

// 가공된 결과 타입
export interface ProcessedRangeResult {
  dateMap: Map<string, ContractBarInfo[]>
  /** 주별 최대 레인 수 (key: 'YYYY-WW' 형식) */
  weekMaxLanesMap: Map<string, number>
}

export const getRanges = (contracts: TRoomContractSchedule[]) => {
  // endDate 기준
  // 1. 퇴실일 없음: 계약 종료일
  // 2. 퇴실일이 계약 종료일보다 빠른 경우: 퇴실일
  // 3. 퇴실일이 계약 종료일보다 느린 경우: 계약 종료일 (퇴실하기를 늦게 누르는 경우)
  return contracts.map(
    ({ initialContractId, startDate, endDate, checkoutDate }) => {
      let effectiveEndDate = endDate

      const isCheckoutDateBeforeEndDate =
        checkoutDate &&
        new Date(checkoutDate).getTime() < new Date(endDate).getTime()

      if (isCheckoutDateBeforeEndDate) {
        effectiveEndDate = checkoutDate
      }

      return {
        initialContractId,
        startDate,
        endDate: effectiveEndDate,
      }
    },
  )
}

export const buildSortedRanges = (ranges: Range[]) => {
  return [...ranges].sort((a, b) => {
    const startCompare =
      parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime()

    if (startCompare !== 0) return startCompare

    return parseISO(a.endDate).getTime() - parseISO(b.endDate).getTime()
  })
}

export const buildProcessedRanges = (sortedRanges: Range[]) => {
  const lanes: { endDate: Date }[] = []
  const processedRanges: ProcessedRange[] = []

  let colorIndex: 0 | 1 = 1
  let lastAssignedLane = -1 // 이전 계약이 배치된 레인

  for (const range of sortedRanges) {
    const rangeStart = parseISO(range.startDate)
    const rangeEnd = parseISO(range.endDate)

    // 사용 가능한 레인 찾기 (종료일이 현재 시작일 이전인 레인)
    let assignedLane = -1
    const minLane = Math.max(0, lastAssignedLane) // 최소 레인 (이전 계약 레인 이상)

    for (let i = minLane; i < lanes.length; i++) {
      if (isBefore(lanes[i].endDate, rangeStart)) {
        assignedLane = i
        break
      }
    }

    // 사용 가능한 레인이 없으면 새 레인 추가
    // 기본은 첫 번째 색상. 계약이 겹쳐 새 레인 추가하는 경우 색상 교체.
    if (assignedLane === -1) {
      colorIndex = colorIndex === 0 ? 1 : 0

      assignedLane = lanes.length
      lanes.push({ endDate: rangeEnd })
    } else {
      colorIndex = 0

      lanes[assignedLane].endDate = rangeEnd
    }

    processedRanges.push({
      ...range,
      laneIndex: assignedLane,
      colorIndex,
    })
    lastAssignedLane = assignedLane
  }

  return processedRanges
}

export const buildWeekActiveRangesMap = (processedRanges: ProcessedRange[]) => {
  const map = new Map<string, Set<number>>()

  for (const processed of processedRanges) {
    const start = parseISO(processed.startDate)
    const end = parseISO(processed.endDate)
    const days = eachDayOfInterval({ start, end })

    for (const day of days) {
      const weekKey = getWeekKey(day)

      if (!map.has(weekKey)) {
        map.set(weekKey, new Set())
      }
      map.get(weekKey)!.add(processed.laneIndex)
    }
  }

  return map
}

export const buildWeeklyLaneMapping = (
  weekActiveRangesMap: Map<string, Set<number>>,
) => {
  const map = new Map<string, Map<number, number>>()

  for (const [weekKey, activeLanes] of weekActiveRangesMap) {
    // 활성화된 레인들을 laneIndex 순으로 정렬
    const sortedLanes = Array.from(activeLanes).sort((a, b) => a - b)

    // 주별 레인 인덱스 매핑 (0부터 연속적으로)
    const laneMap = new Map<number, number>()

    sortedLanes.forEach((globalLane, weeklyLane) => {
      laneMap.set(globalLane, weeklyLane)
    })

    map.set(weekKey, laneMap)
  }

  return map
}

export const buildDateMap = (
  processedRanges: ProcessedRange[],
  weeklyLaneMapping: Map<string, Map<number, number>>,
) => {
  const map = new Map<string, ContractBarInfo[]>()

  for (const processed of processedRanges) {
    const start = parseISO(processed.startDate)
    const end = parseISO(processed.endDate)
    const days = eachDayOfInterval({ start, end })

    for (const day of days) {
      const dateKey = getDateKey(day)
      const weekKey = getWeekKey(day)
      const isStart = isSameDay(day, start)
      const isEnd = isSameDay(day, end)

      // 해당 주에서의 레인 인덱스 조회
      const weeklyLaneIndex =
        weeklyLaneMapping.get(weekKey)?.get(processed.laneIndex) ?? 0

      const info: ContractBarInfo = {
        rangeId: processed.initialContractId,
        laneIndex: processed.laneIndex,
        weeklyLaneIndex,
        colorIndex: processed.colorIndex,
        isStart,
        isEnd,
      }

      if (!map.has(dateKey)) {
        map.set(dateKey, [])
      }
      map.get(dateKey)!.push(info)
    }
  }

  // 각 날짜의 범위들을 weeklyLaneIndex 기준 정렬
  for (const [, infos] of map) {
    infos.sort((a, b) => a.weeklyLaneIndex - b.weeklyLaneIndex)
  }

  return map
}

export const buildWeekMaxLanesMap = (
  dateMap: Map<string, ContractBarInfo[]>,
) => {
  const map = new Map<string, number>()

  for (const [dateKey, infos] of dateMap) {
    const date = parseISO(dateKey)
    const weekKey = getWeekKey(date)
    const maxLaneInDay =
      Math.max(...infos.map(info => info.weeklyLaneIndex)) + 1

    const currentMax = map.get(weekKey) ?? 0

    if (maxLaneInDay > currentMax) {
      map.set(weekKey, maxLaneInDay)
    }
  }

  return map
}

// 특정 날짜에 그려야 할 계약 바 정보 조회
export const getContractBarsForDate = (
  dateMap: Map<string, ContractBarInfo[]>,
  date: Date,
): ContractBarInfo[] => {
  const dateKey = getDateKey(date)

  return dateMap.get(dateKey) || []
}

// 일자 키 생성 (yyyy-MM-dd)
const getDateKey = (date: Date) => {
  return format(date, 'yyyy-MM-dd')
}

// 주 키 생성 (YYYY-WW)
export const getWeekKey = (date: Date) => {
  const year = getYear(date)
  const week = getWeek(date, { weekStartsOn: 0 }) // 일요일 시작

  return `${year}-${week.toString().padStart(2, '0')}`
}

// 특정 날짜가 속한 주의 최대 레인 수 조회
export const getWeekMaxLanes = (
  weekMaxLanesMap: Map<string, number>,
  date: Date,
) => {
  const weekKey = getWeekKey(date)

  return weekMaxLanesMap.get(weekKey) ?? 0
}
